/* eslint-disable new-cap */
/* eslint-disable max-len */
import { connectionData } from './connection.data';
import { Island } from '../islands/island.class';
import { Obstruction } from '../obstructions/obstruction.class';
import { Panel } from '../panels/panel.class';
import { Publish } from '../../decorators';
import { Vertex3 } from '../../spatial-classes';

import { BlueprintInstanceType, HorizontalAlignment, VerticalAlignment } from '../shared.enums';
import { ConnectionDataIn, ConnectionDataOut, ConnectionParams } from './types';
import { getAlignedPoint, moveSourceIslandCfn } from './functions';
import { KitchenDesignBase, KitchenDesignOptions } from '../kitchen-design-base';

/**
 * Represents a connection between panels and islands in the kitchen design.
 * Handles alignment, padding, offset, and serialization of connection data.
 */
export class Connection extends KitchenDesignBase<ConnectionDataIn> {
	#isSource: boolean;
	#padding: number;
	#offset: number;
	#sourcePanelId: string;
	#targetIslandId: string;
	#horizontalAlign: HorizontalAlignment;
	#verticalAlign: VerticalAlignment;
	#alignToPanel: boolean;

	/**
	 * Creates a new Connection instance.
	 * @param params - Connection parameters.
	 * @param options - Kitchen design options.
	 */
	constructor (
		params: ConnectionParams | null = null,
		options: KitchenDesignOptions<ConnectionDataIn> = {}
	) {
		super(BlueprintInstanceType.Connection, options);
		const data = connectionData(params, options.dataIn || null);

		this._id = data.id;
		this.#isSource = data.isSource;
		this.#padding = data.padding;
		this.#offset = data.offset;
		this.#sourcePanelId = data.sourcePanelId;
		this.#targetIslandId = data.targetIslandId;

		this.#verticalAlign = data.verticalAlign;
		this.#horizontalAlign = data.horizontalAlign;
		this.#alignToPanel = data.alignToPanel;

		// console.log('new Connection', this, options);
		this.setInitialized();
	}

	/**
	 * Indicates if this connection is the source.
	 */
	get isSource (): boolean { return this.#isSource }

	/**
	 * Sets the horizontal alignment for the connection.
	 * @param alignment - The horizontal alignment.
	 */
	@Publish('connection_vertical_align', 'updated')
	set horizontalAlign (alignment: HorizontalAlignment) {
		this.#horizontalAlign = alignment;
		moveSourceIslandCfn(this as Connection);
	}
	/**
	 * Gets the horizontal alignment for the connection.
	 */
	get horizontalAlign (): HorizontalAlignment { return this.#horizontalAlign }

	/**
	 * Sets the vertical alignment for the connection.
	 * @param alignment - The vertical alignment.
	 */
	@Publish('connection_vertical_align', 'updated')
	set verticalAlign (alignment: VerticalAlignment) {
		this.#verticalAlign = alignment;
		moveSourceIslandCfn(this as Connection);
	}
	/**
	 * Gets the vertical alignment for the connection.
	 */
	get verticalAlign (): VerticalAlignment { return this.#verticalAlign }

	/**
	 * Sets the offset for the connection.
	 * @param offset - The offset value.
	 */
	@Publish('connection_offset', 'updated')
	set offset (offset: number) {
		this.#offset = offset
		moveSourceIslandCfn(this as Connection);
	}
	/**
	 * Gets the offset for the connection (only if isSource).
	 */
	get offset (): number { return this.#isSource ? this.#offset : 0; }

	/**
	 * Sets the padding for the connection.
	 * @param padding - The padding value.
	 */
	@Publish('connection_padding', 'updated')
	set padding (padding: number) {
		this.#padding = padding
		moveSourceIslandCfn(this as Connection);
	}
	/**
	 * Gets the padding for the connection (only if isSource).
	 */
	get padding (): number { return this.#isSource ? this.#padding : 0; }

	/**
	 * Gets whether the connection aligns to the panel.
	 */
	get alignToPanel (): boolean { return this.#alignToPanel; }

	/**
	 * Gets the ID of the source island.
	 */
	get sourceIslandId (): string { return this.sourceIsland?.id ?? null; }

	/**
	 * Gets the source island instance.
	 */
	get sourceIsland (): Island | Obstruction | null {
		const result = this.refMap.island ?? this.refMap.obstruction ?? null;
		return result;
	}

	/**
	 * Sets the source panel ID for the connection.
	 * @param panelId - The panel ID to set as source.
	 */
	@Publish('connection_source_panel', 'updated')
	set sourcePanelId (panelId: string) {
		if (!this.sourceIsland) return;

		// Both Island and Obstruction provide panels.findById() interface
		if (this.sourceIsland.panels.findById(panelId) !== null) {
			this.#sourcePanelId = panelId;
			moveSourceIslandCfn(this as Connection);
		}
	}
	/**
	 * Gets the source panel ID for the connection.
	 */
	get sourcePanelId (): string { return this.#sourcePanelId }

	/**
	 * Gets the source panel instance for the connection.
	 */
	get sourcePanel (): Panel | null {
		return this.sourceIsland?.panels?.findById(this.#sourcePanelId) ?? null;
	}

	/**
	 * Gets the ID of the target island.
	 */
	get targetIslandId (): string { return this.#targetIslandId }

	/**
	 * Gets the target island instance.
	 */
	get targetIsland (): Island | Obstruction | null {
		const targetIsland = this.refMap.islands?.findById(this.#targetIslandId);
		if (targetIsland) return targetIsland;

		const targetObstruction = this.refMap.obstructions?.findById(this.#targetIslandId);
		return targetObstruction ?? null;
	}

	/**
	 * Gets the ID of the target panel.
	 */
	get targetPanelId (): string { return this.targetPanel?.id ?? null }

	/**
	 * Gets the target panel instance for the connection.
	 */
	get targetPanel (): Panel | null {
		// console.log('Connection:targetPanel', this.targetIsland, this.targetIsland?.connections.findConnectionsByTargetIslandId(this.sourceIslandId)?.sourcePanel, this.sourceIslandId);
		return this.targetIsland?.connections.findConnectionsByTargetIslandId(this.sourceIslandId)?.sourcePanel as Panel ?? null;
	}

	/**
	 * Gets the target connection instance (the connection on the target island that points back to this source).
	 */
	get targetConnection (): Connection | null {
		return this.targetIsland?.connections?.findConnectionsByTargetIslandId(this.sourceIslandId) as Connection ?? null;
	}

	/**
	 * Gets the source point (vertex) for this connection, based on alignment and panel geometry.
	 * @returns The aligned source point as a Vertex3.
	 */
	public getSourcePoint (): Vertex3 {
		if (!this.sourcePanel) return new Vertex3();

		// Try getCornerPoints first if alignToPanel, fall back to getSidePoints if null (obstruction panels)
		// Use nearestSixteenth: false for full precision — rounding connection points to 1/16"
		// shifts island positions by up to 1/16", causing bounding box dimension errors.
		const cornerPoints = this.#alignToPanel
			? (this.sourcePanel.getCornerPoints(false, { translate: true, rotate: true, nearestSixteenth: false }, new Set()) ?? this.sourcePanel.getSidePoints({ translate: true, rotate: true }))
			: this.sourcePanel.getSidePoints({ translate: true, rotate: true });

		return getAlignedPoint(cornerPoints.corners, this.#horizontalAlign, this.#verticalAlign);
	}

	/**
	 * Gets the target point (vertex) for this connection, based on alignment and panel geometry.
	 * Horizontal alignment is mirrored for the target since two facing panels present as mirror
	 * images along that axis. Vertical alignment is NOT mirrored -- both islands sit on the same
	 * floor datum, so "bottom" must mean the same physical height on both sides of the connection.
	 * @returns The aligned target point as a Vertex3.
	 */
	public getTargetPoint (): Vertex3 {
		if (!this.targetPanel) return new Vertex3();

		// Try getCornerPoints first if alignToPanel, fall back to getSidePoints if null (obstruction panels)
		// Use nearestSixteenth: false for full precision — see getSourcePoint comment.
		const cornerPoints = this.#alignToPanel
			? (this.targetPanel.getCornerPoints(false, { translate: true, rotate: true, nearestSixteenth: false }, new Set()) ?? this.targetPanel.getSidePoints({ translate: true, rotate: true }))
			: this.targetPanel.getSidePoints({ translate: true, rotate: true });

		return getAlignedPoint(
			cornerPoints.corners,
			this.#horizontalAlign === HorizontalAlignment.left ? HorizontalAlignment.right : (this.#horizontalAlign === HorizontalAlignment.right ? HorizontalAlignment.left : this.#horizontalAlign),
			this.#verticalAlign
		);
	}

	/**
	 * Validates the connection.
	 * @returns True if the connection is valid, false if not, or null if validation cannot be performed.
	 */
	public validate (): boolean | null {
		// can't validate if no parent exist
		if (this.instanceParent === null) return null;

		return this.sourcePanel !== null && this.targetIsland !== null;
	}

	/**
	 * Serializes the connection to a plain object suitable for storage or transmission.
	 * @returns The serialized connection data.
	 */
	public serialize (): ConnectionDataOut {
		return {
			id: this._id,
			isSource: this.#isSource,
			verticalAlign: this.verticalAlign,
			horizontalAlign: this.horizontalAlign,
			alignToPanel: this.#alignToPanel,
			padding: this.#padding,
			offset: this.#offset,
			sourcePanelId: this.#sourcePanelId,
			targetIslandId: this.#targetIslandId
		};
	}
}
